import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import {
  useGetMyExamCallApplicationsQuery,
  useSettleExamPaymentMutation,
} from "../../api/examCallsApi";

/**
 * Landing page OnePay redirects to after the exam fee is paid. It polls the
 * member's applications until the verify job flips this one to 'paid', then
 * sends them on to finalize (pick center + papers/courses).
 */
export default function ExamPaymentProcessingPage() {
  const { appId = "" } = useParams();
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);

  const { data: apps = [], refetch } = useGetMyExamCallApplicationsQuery(undefined, {
    pollingInterval: 4000,
  });

  const [settle] = useSettleExamPaymentMutation();

  const app = useMemo(() => apps.find((a: any) => a.id === appId), [apps, appId]);
  const status = app?.status;

  // Drive the verification ourselves rather than only watching for it. The
  // gateway callback queues the verify job, so a stalled worker leaves a member
  // who HAS paid staring at this page forever. Asking once on arrival, and
  // again if it is still not settled after 10s, means the money lands without
  // anyone having to go and reverify it by hand.
  useEffect(() => {
    if (!appId) return;
    settle(appId);
  }, [appId, settle]);

  useEffect(() => {
    if (!appId || status !== "pending_payment") return;
    const t = setTimeout(() => settle(appId), 10000);
    return () => clearTimeout(t);
  }, [appId, status, settle]);

  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (status === "paid") {
      navigate(`/exams/applications/${appId}/finalize`, { replace: true });
    }
  }, [status, appId, navigate]);

  const alreadyDone = status === "submitted" || status === "approved" || status === "completed";
  const slow = elapsed > 30 && status !== "paid";

  return (
    <div className="max-w-lg mx-auto p-6">
      <div className="bg-white border rounded-2xl p-8 text-center shadow-sm">
        {alreadyDone ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-lg font-bold text-gray-900 mt-4">Payment received</h1>
            <p className="text-sm text-gray-500 mt-1">This application has already been submitted.</p>
            <Link
              to="/exams/calls/applications"
              className="inline-block mt-5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800"
            >
              Go to My Applications
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-indigo-500 mx-auto animate-spin" />
            <h1 className="text-lg font-bold text-gray-900 mt-4">Confirming your payment…</h1>
            <p className="text-sm text-gray-500 mt-1">
              This can take a few moments. Please keep this page open.
            </p>

            {slow ? (
              <div className="mt-5 text-left text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 flex gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Still confirming. If you completed payment, it should clear shortly. You can{" "}
                  <button
                    onClick={async () => {
                      // Verify with the gateway, then re-read — refetching
                      // alone only re-asks a status nothing has changed.
                      await settle(appId);
                      refetch();
                    }}
                    className="underline font-semibold"
                  >
                    check again
                  </button>{" "}
                  or return to{" "}
                  <Link to="/exams/calls/applications" className="underline font-semibold">
                    My Applications
                  </Link>
                  .
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
